import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import { type ReactElement, type ReactNode, useEffect, useRef } from 'react';
import {
  FormProvider,
  type FieldValues,
  type Resolver,
  type SubmitHandler,
  type UseFormProps,
  type UseFormReturn,
  useForm,
} from 'react-hook-form';
import { Stack } from '@chakra-ui/react';

export type FormChildren<TFormValues extends FieldValues> =
  | ReactNode
  | ((methods: UseFormReturn<TFormValues>) => ReactNode);

export type FormProps<
  TFormValues extends FieldValues,
  Schema extends Joi.ObjectSchema = Joi.ObjectSchema,
> = {
  autoComplete?: 'off' | 'on';
  /** Plain nodes use `useFormContext` inside (e.g. `RHFFormInput`); pass a function if you need `methods` exposed. */
  children: FormChildren<TFormValues>;
  className?: string;
  gap?: number;
  id?: string;
  onSubmit: SubmitHandler<TFormValues>;
  options?: UseFormProps<TFormValues>;
  schema?: Schema;
  /** Runs after the form initializes; use for validating prefilled values. */
  onMount?: (methods: UseFormReturn<TFormValues>) => void | Promise<void>;
  /** Re-runs {@link onMount} when this key changes (e.g. async prefill load). */
  onMountKey?: string;
};

/**
 * React Hook Form + Joi. Wraps fields in {@link FormProvider} for `useFormContext`.
 */
export const Form = <
  TFormValues extends FieldValues = FieldValues,
  Schema extends Joi.ObjectSchema = Joi.ObjectSchema,
>({
  autoComplete = 'off',
  children,
  className,
  gap = 3,
  id,
  onSubmit,
  options,
  schema,
  onMount,
  onMountKey = 'initial',
}: FormProps<TFormValues, Schema>): ReactElement => {
  const methods = useForm<TFormValues>({
    ...options,
    mode: 'onChange',
    resolver: schema ? (joiResolver(schema) as Resolver<TFormValues>) : undefined,
  });

  const onMountRef = useRef(onMount);
  onMountRef.current = onMount;

  useEffect(() => {
    onMountRef.current?.(methods);
  }, [methods, onMountKey]);

  // Enable exposing `methods` via function for custom inputs or fall back to plain nodes.
  const body = typeof children === 'function' ? children(methods) : children;

  return (
    <FormProvider {...methods}>
      <form
        className={className}
        id={id}
        autoComplete={autoComplete}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <Stack gap={gap}>{body}</Stack>
      </form>
    </FormProvider>
  );
};
